import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DataService } from 'src/app/shared/network/data.service';
import { ColumnForSelection } from 'src/app/shared/import/column-for-selection';
import { BaseColumnSelectComponent } from '../base-column-selection.component';
import { ProjectService } from 'src/app/shared/project/project.service';
import { ImportProgressService } from 'src/app/shared/import/import-progress.service';
import { DimensionsSelectionModel } from 'src/app/shared/import/dimensions-selection-model';
import { ImportStep } from '../../import-step';
import { DimensionSelectionResult } from './dimensions-selection-result';
import { MatDialog } from '@angular/material/dialog';
import { RedundantModalComponent } from './redundant-modal.component';
import { TopValsModalComponent } from '../../complexity-mgnt/top-vals-modal.component';
import { ColumnScrollService } from '../../column-scroll.service';

@Component({
    selector: 'dimensions-selection',
    templateUrl: './dimensions-columns-selection.component.html',
    styleUrls: ['../../general.scss', '../column-selection.scss']
})
export class DimensionsColumnsSelectComponent extends BaseColumnSelectComponent {
    readonly step = ImportStep.Dimensions;
    projectId: string;
    public benchmarksAvailable: boolean = false;
    public removeMinNRows: number | null;

    loading = true;

    constructor(private dataService: DataService, public dialog: MatDialog, projectService: ProjectService, columnScrollService: ColumnScrollService) {
        super(projectService, columnScrollService);
    }

    public async loadDataInternal(): Promise<void> {
        const resp = await this.dataService.get<DimensionsSelectionModel>('columnSelection/dimensions/' + this.projectId);
        this.stepState = resp.stepState;
        this.columns = resp.columns;
        this.benchmarksAvailable = resp.benchmarksAvailable;
        this.loading = false;
    }

    public toggleSelect(column: ColumnForSelection): void {
        if (!column || !column.isEligible) {
            return;
        }
        this.removeMinNRows = null;
        if (!column.isSelected && column.group !== null) {
            const columnsWithSameGrp = this.columns.filter(c => c.isSelected && c.group === column.group);
            if (columnsWithSameGrp.length > 0) {
                const dialogRef = this.dialog.open(RedundantModalComponent, {
                    data: {
                        oldColumnName: columnsWithSameGrp[0].name,
                        newColumnName: column.name
                    },
                });
                dialogRef.afterClosed().subscribe(result => {
                    if (result) {
                        columnsWithSameGrp.forEach(c => c.isSelected = false);
                        column.isSelected = true;
                    }
                });
                return;
            }
        }
        column.isSelected = !column.isSelected;
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    async save() {
        if (this.loading) {
            return;
        }

        if (this.selectedColumnsCount === 0) {
            return;
        }

        try {
            this.loading = true;
            this.removeMinNRows = null;
            let selectedColumns = [];
            for (let i = 0; i < this.columns.length; ++i) {
                if(this.columns[i].isSelected) {
                    selectedColumns.push(i);
                }
            }
            const res = await this.dataService.post<DimensionSelectionResult>('columnSelection/dimensions', {
                projectId: this.projectId,
                selectedColumns: selectedColumns,
            });
            if (res.complexityEstimationResult.removeMinNRows) {
                this.removeMinNRows = res.complexityEstimationResult.removeMinNRows;
            } else {
                this.finishStepSuccessfully(res.changesApplied, res);
            }
        } finally {
            this.loading = false;
        }
    }
    
    public get selectedColumnsCount(): number {
        if (!this.columns || !this.columns.length) {
            return 0;
        }
        return this.columns.filter(c => c.isSelected).length;   
    }

    public get selectedColumnNames(): string {
        if (!this.columns || !this.columns.length) {
            return '';
        }
        return this.columns.filter(c => c.isSelected).map(c => c.name).join(', ');
    }

    showTopValsDialog(columnName: string) {
        const dialogRef = this.dialog.open(TopValsModalComponent, {
            data: {
                projectId: this.projectId,
                columnName: columnName
            },
        });
        const self = this;
        dialogRef.afterClosed().subscribe(result => {
            self.loadDataInternal();
        });
    }

}
